
import { Shield, Eye, Zap, AlertTriangle, Activity, Network } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const ManagedXdr = () => {
  return (
    <LandingPageTemplate
      title="Managed XDR Services"
      description="Advanced Extended Detection and Response (XDR) services to protect your organization from sophisticated cyber threats."
      keywords={[
        "managed XDR",
        "threat detection",
        "incident response",
        "security monitoring",
        "threat hunting"
      ]}
      canonicalUrl="/cybersecurity/managed-xdr/"
      schemaType="service"
      schemaData={{
        name: "Managed XDR Services",
        description: "Advanced threat detection and response solutions",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Advanced Threat"
        highlighted="Detection & Response"
        description="24/7 managed XDR services to detect, investigate, and respond to sophisticated cyber threats across your entire infrastructure."
        ctaText="Get Protected"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Comprehensive XDR Protection"
        subtitle="KEY FEATURES"
        description="Our managed XDR services provide advanced threat detection and response capabilities."
        features={[
          {
            title: "24/7 Monitoring",
            description: "Continuous threat monitoring and detection.",
            icon: <Eye className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Rapid Response",
            description: "Immediate threat containment and response.",
            icon: <Zap className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Threat Hunting",
            description: "Proactive threat hunting across systems.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Analytics",
            description: "Advanced security analytics and correlation.",
            icon: <Activity className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Alert Triage",
            description: "Expert analysis and alert prioritization.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Integration",
            description: "Unified visibility across security tools.",
            icon: <Network className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our XDR Approach"
        subtitle="METHODOLOGY"
        description="A comprehensive approach to threat detection and response"
        steps={[
          {
            title: "Data Collection",
            description: "Gather security data across all sources"
          },
          {
            title: "Analysis",
            description: "Advanced correlation and threat analysis"
          },
          {
            title: "Detection",
            description: "Identify potential security threats"
          },
          {
            title: "Investigation",
            description: "Expert investigation of security events"
          },
          {
            title: "Response",
            description: "Rapid threat containment and remediation"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our XDR Services"
        benefits={[
          {
            title: "24/7 Protection",
            description: "Round-the-clock security monitoring"
          },
          {
            title: "Expert Team",
            description: "Certified security analysts"
          },
          {
            title: "Advanced Technology",
            description: "Leading XDR platform and tools"
          },
          {
            title: "Rapid Response",
            description: "Quick threat containment"
          },
          {
            title: "Unified View",
            description: "Complete security visibility"
          },
          {
            title: "Continuous Learning",
            description: "Evolving threat detection"
          }
        ]}
      />

      <CtaSection 
        title="Ready for Advanced Threat Protection?"
        description="Schedule a consultation to discuss your XDR security needs."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request XDR Services"
        modalDescription="Tell us about your organization's security monitoring requirements."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Managed XDR",
          service_interest: "Security Services"
        }}
      />
    </LandingPageTemplate>
  );
};

export default ManagedXdr;
